import { useState } from 'react';

import api from '@/services/api';

import { queueFeedback, type QueuedFeedback } from '../lib/offlineDb';

interface SubmitFeedbackParams {
  boothId?: string;
  sessionId?: string;
  category: string;
  comment?: string;
  audioClips?: { id: string; blob: Blob; durationMs: number }[];
  locale: string;
  source: string;
  flowStep?: string;
  lastMessage?: string;
  userEmail?: string;
  userPhone?: string;
  appVersion?: string;
}

export function useSubmitFeedback() {
  const [progress, setProgress] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const [error, setError] = useState<Error | null>(null);

  const submit = async (params: SubmitFeedbackParams) => {
    setProgress('uploading');
    setError(null);

    const clientId = crypto.randomUUID();

    const metadata = {
      boothId: params.boothId || undefined,
      sessionId: params.sessionId || undefined,
      category: params.category,
      comment: params.comment,
      source: params.source,
      locale: params.locale,
      flowStep: params.flowStep,
      lastMessage: params.lastMessage,
      userEmail: params.userEmail,
      userPhone: params.userPhone,
      appVersion: params.appVersion || import.meta.env.VITE_APP_VERSION || undefined,
      clipDurations: params.audioClips?.map(c => c.durationMs) ?? [],
    };

    // Offline: queue to IndexedDB
    if (!navigator.onLine) {
      console.info(
        `[FeedbackQueue] Offline — queueing feedback ${clientId} (category=${params.category})`,
      );
      const item: QueuedFeedback = {
        id: clientId,
        boothId: params.boothId ?? null,
        sessionId: params.sessionId ?? null,
        category: params.category,
        comment: params.comment ?? '',
        audioClips: params.audioClips ?? [],
        locale: params.locale,
        source: params.source,
        flowStep: params.flowStep ?? null,
        lastMessage: params.lastMessage ?? null,
        appVersion: (metadata.appVersion as string) ?? null,
        userEmail: params.userEmail ?? null,
        userPhone: params.userPhone ?? null,
        healthSnapshot: null,
        status: 'QUEUED',
        retryCount: 0,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      await queueFeedback(item);
      console.info(`[FeedbackQueue] Saved to IndexedDB — ${clientId}`);
      setProgress('done');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('metadata', JSON.stringify(metadata));

      if (params.audioClips) {
        for (let i = 0; i < params.audioClips.length; i++) {
          formData.append(`audio${i}`, params.audioClips[i].blob, `clip-${i}.webm`);
        }
      }

      console.info(`[Feedback] Sending feedback ${clientId} online (category=${params.category})`);
      await api.post('/booth-feedback', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Idempotency-Key': clientId,
        },
      });
      console.info(`[Feedback] Sent successfully — ${clientId}`);
      setProgress('done');
      return;
    } catch (err) {
      // Network error while online → queue for retry
      if (!navigator.onLine) {
        console.info(`[FeedbackQueue] Network lost during send — queueing ${clientId}`);
        const item: QueuedFeedback = {
          id: clientId,
          boothId: params.boothId ?? null,
          sessionId: params.sessionId ?? null,
          category: params.category,
          comment: params.comment ?? '',
          audioClips: params.audioClips ?? [],
          locale: params.locale,
          source: params.source,
          flowStep: params.flowStep ?? null,
          lastMessage: params.lastMessage ?? null,
          appVersion: (metadata.appVersion as string) ?? null,
          userEmail: params.userEmail ?? null,
          userPhone: params.userPhone ?? null,
          healthSnapshot: null,
          status: 'QUEUED',
          retryCount: 0,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        await queueFeedback(item);
        console.info(`[FeedbackQueue] Saved to IndexedDB — ${clientId}`);
        setProgress('done');
        return;
      }
      console.error(`[Feedback] Send failed — ${clientId}`, err);
      setProgress('error');
      setError(err instanceof Error ? err : new Error('Failed to submit feedback'));
      throw err;
    }
  };

  const reset = () => {
    setProgress('idle');
    setError(null);
  };

  return { submit, progress, error, reset };
}
